import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { colors, spacing, typography, borderRadius } from '../styles/theme';
import EventFinderAPI from '../services/api';
import type { Event, ResolvedLocation } from '../types/api';
import { SEO } from '../components/SEO';
import { SITE_URL, canonicalUrl, truncateDescription } from '../utils/seo';
import EventsExplorer from '../components/events/EventsExplorer';
import { groupEventsByDuration, groupEventsByRecency } from '../utils/eventPresentation';
import config from '../config';

/**
 * Canonical location events page: /location/<city-slug>/events
 *
 * The slug resolves to a curated `locations.json` city, and the search runs
 * from that city's coordinates — never by matching the city *name* — so events
 * in adjacent municipalities are included and duplicate city names can't
 * resolve to the wrong place.
 */

const RADIUS_OPTIONS_KM = [25, 50, 100, 250];
const DEFAULT_RADIUS_KM = 50;
const PAGE_SIZE = 100;
const FILTER_KEYS = ['days', 'from', 'to', 'format', 'rel', 'tod', 'vibe', 'hideEntity'];

function apiFilters(searchParams: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = searchParams.get(key);
    if (value) params.set(key, value);
  }
  return params;
}

function appendUnique(current: Event[], next: Event[]): Event[] {
  const seen = new Set(current.map((event) => event.eventId));
  return [...current, ...next.filter((event) => !seen.has(event.eventId))];
}

const Breadcrumbs = styled.nav`
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.tertiary};
  margin-bottom: ${spacing[3]};

  a {
    color: ${colors.text.link};
    text-decoration: none;
  }

  a:hover,
  a:focus-visible {
    text-decoration: underline;
  }
`;

const RadiusField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  min-height: 44px;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};

  select {
    min-height: 44px;
    padding: 0 ${spacing[8]} 0 ${spacing[3]};
    border: 1px solid ${colors.border.normal};
    border-radius: ${borderRadius.md};
    background: ${colors.white};
    color: ${colors.text.primary};
    font: inherit;

    &:focus-visible {
      outline: 2px solid ${colors.border.focus};
      outline-offset: 2px;
    }
  }
`;

export const LocationEventsPage: React.FC = () => {
  const { citySlug = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [location, setLocation] = useState<ResolvedLocation | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [vibeOptions, setVibeOptions] = useState<string[] | undefined>(undefined);
  const [entityFilterOptions, setEntityFilterOptions] = useState<Array<{ value: number; label: string }>>([]);
  const loadMoreControllerRef = useRef<AbortController | null>(null);

  const parsedRadius = Number(searchParams.get('radiusKm'));
  const radiusKm = RADIUS_OPTIONS_KM.includes(parsedRadius) ? parsedRadius : DEFAULT_RADIUS_KM;
  const filters = useMemo(() => apiFilters(searchParams), [searchParams]);
  const filterKey = filters.toString();
  const queryKey = `${citySlug}|${radiusKm}|${filterKey}|${reloadToken}`;
  const activeQueryKeyRef = useRef(queryKey);
  activeQueryKeyRef.current = queryKey;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    loadMoreControllerRef.current?.abort();

    setIsLoading(true);
    setIsLoadingMore(false);
    setError(null);
    setNotFound(false);
    setEvents([]);
    setTotalAvailable(0);
    setPage(1);
    setHasMore(false);
    setVibeOptions(undefined);
    setEntityFilterOptions([]);

    (async () => {
      try {
        const isCurrentLocation = location?.slug === citySlug
          || location?.canonicalUrl === window.location.pathname;
        let resolvedLocation = isCurrentLocation ? location : null;
        if (!resolvedLocation) {
          const resolution = await EventFinderAPI.resolveLocation({ slug: citySlug }, controller.signal);
          if (cancelled) return;

          if (!resolution) {
            setNotFound(true);
            setIsLoading(false);
            return;
          }

          if (resolution.redirectTo && resolution.redirectTo !== window.location.pathname) {
            navigate({ pathname: resolution.redirectTo, search: searchParams.toString() }, { replace: true });
            return;
          }

          resolvedLocation = resolution.location;
          setLocation(resolvedLocation);
        }

        const response = await EventFinderAPI.getEventsByCity(resolvedLocation.city, {
          state: resolvedLocation.adminName || resolvedLocation.state || undefined,
          country: resolvedLocation.country,
          radiusKm,
          page: 1,
          limit: PAGE_SIZE,
          sortBy: 'startDate',
          sortDirection: 'ASC',
          filters: new URLSearchParams(filterKey),
          signal: controller.signal,
        });
        if (cancelled || activeQueryKeyRef.current !== queryKey) return;

        setEvents(Array.isArray(response.events) ? response.events : []);
        setTotalAvailable(response.pagination?.totalItems ?? 0);
        setPage(response.pagination?.page ?? 1);
        setHasMore(Boolean(response.pagination?.hasNextPage));
        setVibeOptions(response.facets?.vibes);
        setEntityFilterOptions((response.facets?.entities ?? []).map((entity) => ({
          value: entity.entityId,
          label: entity.displayName,
        })));
        setIsLoading(false);
      } catch (err) {
        if (cancelled || (err as Error)?.name === 'AbortError') return;
        console.error('Failed to load location events', err);
        setError(`We could not load these events. ${(err as Error)?.message || ''}`.trim());
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      loadMoreControllerRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySlug, radiusKm, filterKey, reloadToken, navigate]);

  const handleRetry = useCallback(() => setReloadToken((token) => token + 1), []);

  const handleLoadMore = useCallback(async () => {
    if (!location || !hasMore || isLoading || isLoadingMore) return;

    const requestQueryKey = queryKey;
    const controller = new AbortController();
    loadMoreControllerRef.current?.abort();
    loadMoreControllerRef.current = controller;
    setIsLoadingMore(true);

    try {
      const response = await EventFinderAPI.getEventsByCity(location.city, {
        state: location.adminName || location.state || undefined,
        country: location.country,
        radiusKm,
        page: page + 1,
        limit: PAGE_SIZE,
        sortBy: 'startDate',
        sortDirection: 'ASC',
        filters: new URLSearchParams(filterKey),
        signal: controller.signal,
      });
      if (activeQueryKeyRef.current !== requestQueryKey) return;

      setEvents((current) => appendUnique(current, Array.isArray(response.events) ? response.events : []));
      setTotalAvailable(response.pagination?.totalItems ?? totalAvailable);
      setPage(response.pagination?.page ?? page + 1);
      setHasMore(Boolean(response.pagination?.hasNextPage));
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        console.error('Failed to load more location events', err);
      }
    } finally {
      if (loadMoreControllerRef.current === controller) loadMoreControllerRef.current = null;
      if (activeQueryKeyRef.current === requestQueryKey) setIsLoadingMore(false);
    }
  }, [filterKey, hasMore, isLoading, isLoadingMore, location, page, queryKey, radiusKm, totalAvailable]);

  const handleRadiusChange = useCallback(
    (next: number) => {
      const params = new URLSearchParams(searchParams);
      if (next === DEFAULT_RADIUS_KM) params.delete('radiusKm');
      else params.set('radiusKm', String(next));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  if (!citySlug) {
    return <Navigate to="/" replace />;
  }

  const { upcoming: currentEvents } = groupEventsByRecency(events);
  const { upcoming, ongoing } = groupEventsByDuration(
    currentEvents,
    config.eventDisplay.ongoingDurationHours,
  );
  const currentEventCount = upcoming.length + ongoing.length;
  const displayName = location?.displayName ?? '';
  const shortName = location?.shortDisplayName ?? displayName;
  const title = location ? `Events near ${shortName}` : 'Events';
  const canonicalPath = location?.canonicalUrl || `/location/${citySlug}/events`;
  const description = location
    ? truncateDescription(
        `${upcoming.length} upcoming and ${ongoing.length} ongoing ${currentEventCount === 1 ? 'event' : 'events'} within ${radiusKm} km of ${displayName}, including nearby municipalities.`,
      )
    : undefined;

  const breadcrumbData = location
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            { '@type': 'ListItem', position: 2, name: location.country, item: canonicalUrl('/browse') },
            { '@type': 'ListItem', position: 3, name: shortName, item: canonicalUrl(canonicalPath) },
          ],
        },
      ]
    : [];

  return (
    <>
      <SEO
        title={title}
        description={description}
        canonical={canonicalUrl(canonicalPath)}
        structuredData={breadcrumbData}
        noIndex={notFound}
      />
      <EventsExplorer
        title={title}
        subtitle={
          location
            ? `${displayName} · ${upcoming.length} upcoming · ${ongoing.length} ongoing within ${radiusKm} km`
            : undefined
        }
        breadcrumbs={
          <Breadcrumbs aria-label="Breadcrumb">
            <a href="/">Home</a>
            {location && (
              <>
                {' / '}
                <span>{location.country}</span>
                {' / '}
                <span aria-current="page">{shortName}</span>
              </>
            )}
          </Breadcrumbs>
        }
        events={events}
        isLoading={isLoading}
        error={notFound ? `We could not find a location at /location/${citySlug}/events.` : error}
        onRetry={notFound ? undefined : handleRetry}
        totalAvailable={totalAvailable}
        fetchLimit={PAGE_SIZE}
        exportLabel={shortName || citySlug}
        emptyMessage={`No upcoming or recent events within ${radiusKm} km of ${shortName}. Try a wider radius.`}
        telemetryCategory="location_events"
        showDurationTabs
        remoteFiltering
        resultTotal={totalAvailable}
        vibeOptionsOverride={vibeOptions}
        entityFilterOptions={entityFilterOptions}
        hasMoreUpcoming={hasMore}
        isLoadingMoreUpcoming={isLoadingMore}
        onLoadMoreUpcoming={handleLoadMore}
        toolbarExtra={
          <RadiusField>
            Distance
            <select
              aria-label="Search radius"
              value={radiusKm}
              onChange={(event) => handleRadiusChange(Number(event.target.value))}
            >
              {RADIUS_OPTIONS_KM.map((option) => (
                <option key={option} value={option}>{`${option} km`}</option>
              ))}
            </select>
          </RadiusField>
        }
      />
    </>
  );
};

export default LocationEventsPage;
